import { BusyContent } from '../ui/Primitives';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './MeetingLocationSettings.css';

const videoKinds = new Set(['google_conference', 'zoom_conference', 'microsoft_teams_conference', 'webex_conference', 'gotomeeting_conference']);

export function MeetingConfirmationSettings({ capabilities, value, onChange, disabled, children }) {
  const settings = value || {};
  const provider = capabilities?.provider;
  const kind = settings.kind || (provider === 'calendly' ? 'default' : 'none');
  const includedWithLink = videoKinds.has(kind) || kind === 'custom';
  const sendsInvitation = Boolean(settings.sendInvites || includedWithLink);
  const saveInvites = event => onChange({ provider, kind, location: settings.location || '', sendInvites: event.target.checked }).catch(() => {});
  return (
    <section className="meeting-confirmations" aria-labelledby="meeting-confirmations-heading">
      <h4 id="meeting-confirmations-heading">Confirmations</h4>
      {provider === 'google' && <label className={`appointment-text-option ${sendsInvitation ? 'is-enabled' : ''}`}>
        <span className="appointment-text-option-icon" aria-hidden="true">Email</span>
        <span className="appointment-text-option-copy">
          <span className="appointment-text-option-title"><strong>Send calendar invitations</strong><small>{sendsInvitation ? 'On' : 'Off'}</small></span>
          <span id="appointment-email-invitation-help">{includedWithLink
            ? 'Included with this meeting link. The receptionist must collect and confirm the caller’s email before booking.'
            : 'Sending an invitation requires the caller’s email address. The receptionist will collect and confirm it before booking.'}</span>
        </span>
        <span className="appointment-text-option-switch">
          <input type="checkbox" role="switch" aria-label="Send calendar invitations" aria-describedby="appointment-email-invitation-help"
            checked={sendsInvitation} disabled={disabled || includedWithLink} onChange={saveInvites} />
          <span aria-hidden="true" />
        </span>
      </label>}
      {provider === 'calendly' && <p className="meeting-email-summary">
        <strong>Calendly confirmations</strong>
        <span>Calendly requires the caller’s email to book. Invitations and other emails follow your Calendly settings.</span>
      </p>}
      {children}
    </section>
  );
}

export default function MeetingLocationSettings({ organizationId, capabilities, value, onChange, disabled }) {
  const settings = value || {};
  const provider = capabilities?.provider;
  const eventType = capabilities?.eventType;
  const roundRobin = eventType?.pooling_type === 'round_robin';
  const configuredOptions = capabilities?.meetingOptions || [];
  const options = configuredOptions.filter((option, index, all) => (
    (!videoKinds.has(option.kind) || option.connected === true)
    && all.findIndex(candidate => candidate.kind === option.kind && (candidate.location || '') === (option.location || '')) === index
  ));
  const kind = settings.kind || (provider === 'calendly' ? 'default' : 'none');
  const usesSingleEventLocation = provider === 'calendly' && kind === 'default' && configuredOptions.length === 1 && options.length === 1;
  const selected = usesSingleEventLocation ? 0 : options.findIndex(option => option.kind === kind && (provider !== 'calendly' || (option.location || '') === (settings.location || '')));
  const selectedValue = roundRobin ? (kind === 'default' && options.length ? 'default' : '') : selected < 0 ? '' : String(selected);
  const forcedInvites = provider === 'calendly' || videoKinds.has(kind) || kind === 'custom';
  const [issues, setIssues] = useState([]);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('jwt_authorization')}` });
  const issuesUrl = `${process.env.REACT_APP_BACKEND_HOST}/v2/organization/${organizationId}/appointment_issues`;

  useEffect(() => {
    let active = true;
    let timer;
    const load = async () => {
      try {
        const { data } = await axios.get(issuesUrl, { headers: headers() });
        if (!active) return;
        setIssues(data.issues || []);
        setError('');
        if ((data.issues || []).some(issue => issue.conferenceStatus === 'pending' || ['creating', 'unknown'].includes(issue.status))) {
          timer = setTimeout(load, 30000);
        }
      } catch (_error) {
        if (active) setError('Appointment status could not be loaded. Try refreshing.');
      }
    };
    load();
    return () => { active = false; clearTimeout(timer); };
  }, [issuesUrl, refreshKey]);

  const save = changes => onChange({ provider, kind, location: settings.location || '', sendInvites: Boolean(settings.sendInvites || forcedInvites), ...changes }).catch(() => {});
  const select = event => {
    if (!event.target.value) return;
    if (event.target.value === 'default') {
      save({ kind: 'default', location: '', sendInvites: true });
      return;
    }
    const option = options[Number(event.target.value)];
    if (option) save({ kind: option.kind, location: option.location || '', sendInvites: Boolean(settings.sendInvites || provider === 'calendly' || videoKinds.has(option.kind) || option.kind === 'custom') });
  };
  const recheck = async id => {
    setChecking(id);
    try {
      await axios.post(`${issuesUrl}/${id}/recheck`, {}, { headers: headers() });
      setRefreshKey(key => key + 1);
    } catch (_error) {
      setError('The video link could not be queued for another check.');
    } finally {
      setChecking(null);
    }
  };
  return (
    <section className="meeting-location-settings" aria-labelledby="meeting-location-heading">
      <div className="meeting-location-heading">
        <div><h4 id="meeting-location-heading">Meeting location</h4><p>Choose how callers will attend. Changes apply to future bookings after publishing.</p></div>
      </div>
      {!provider ? <p role="status">Meeting options appear when a calendar is connected.</p> : (
        <fieldset disabled={disabled || !capabilities}>
          <label htmlFor="appointment-meeting-location">Location or video service</label>
          <select id="appointment-meeting-location" disabled={!options.length || (provider === 'calendly' && !eventType)} value={selectedValue} onChange={select}>
            {selectedValue === '' && <option value="" disabled>{provider === 'calendly' && !eventType ? 'Select an event type above' : !options.length ? 'No available locations' : 'Choose a meeting location'}</option>}
            {roundRobin && options.length > 0 && <option value="default">{options.length === 1 ? options[0].label : 'Assigned host’s location'}</option>}
            {!roundRobin && options.map((option, index) => <option key={`${option.kind}-${index}`} value={String(index)}>{option.label}{option.location ? ` · ${option.location}` : ''}</option>)}
          </select>
          {provider === 'google' && ['physical', 'inbound_call', 'custom'].includes(kind) && (
            <label className="meeting-location-detail">{kind === 'physical' ? 'Meeting address' : kind === 'inbound_call' ? 'Phone number to call' : 'HTTPS participant meeting link'}
              <input type={kind === 'custom' ? 'url' : 'text'} maxLength={1000} value={settings.location || ''} onChange={event => save({ location: event.target.value })} />
              {kind === 'custom' && <small>This supplied link is reused for every booking. Use a participant link, never a host/start link.</small>}
            </label>
          )}
          {kind === 'outbound_call' && <p>The receptionist will collect the caller’s callback number.</p>}
          {settings.provider && settings.provider !== provider && <p className="meeting-warning" role="alert">Your calendar connection changed. Select and publish a meeting location again.</p>}
        </fieldset>
      )}
      {error && <p role="alert" className="meeting-warning">{error}</p>}
      {issues.length > 0 && <div className="meeting-issues"><h4>Appointments needing attention</h4>{issues.map(issue => (
        <div className="meeting-issue" key={issue.id}>
          <strong>{issue.status !== 'booked' ? 'Booking needs verification' : issue.conferenceStatus === 'pending' ? 'Booked · video link pending' : 'Booked · video link needs attention'}</strong>
          <span>{new Date(issue.startAt).toLocaleString()} · {issue.provider === 'google' ? 'Google Calendar' : 'Calendly'}</span>
          <p>{issue.status !== 'booked' ? 'Check the provider before making another reservation.' : 'Repair the existing event or conferencing connection. Do not create another appointment.'}</p>
          <div className="meeting-issue-actions">
            <a href={issue.eventUrl || (issue.provider === 'google' ? 'https://calendar.google.com/' : 'https://calendly.com/app/scheduled_events/user/me')} target="_blank" rel="noopener noreferrer">Review calendar ↗</a>
            {issue.status === 'booked' && <button type="button" className="meeting-secondary-button" disabled={checking === issue.id || issue.conferenceStatus === 'pending'} onClick={() => recheck(issue.id)}><BusyContent busy={checking === issue.id} label={"Queuing…"}>{'Recheck video link'}</BusyContent></button>}
          </div>
        </div>
      ))}</div>}
    </section>
  );
}
